import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { Box, HStack } from "@chakra-ui/layout";
import { Checkbox, VStack } from "@chakra-ui/react";
import React, { useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import { MembershipRenewalTable } from "../../components/MembershipRenewal/MembershipRenewalTable";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { useAdminApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import { useCheckboxState } from "../../hooks/useCheckboxState";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { useSearchInput } from "../../hooks/useSearchInput";
import { useToast } from "../../hooks/useToast";
import { PlusIcon } from "../../styles/icons";

export const MembershipRenewals = () => {
  const business = useBusinessContext();
  const navigate = useNavigate();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();
  const { showErrorToast, showSuccessToast } = useToast();
  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Search membership renewal forms...",
  });

  const {
    checked: showDeletedMembershipRenewal,
    onChange: onChangeDeletedMembershipRenewalCheck,
  } = useCheckboxState(false);

  const {
    items: membershipRenewals,
    refresh,
    isLoading,
  } = usePaginatedAdminApiRequest(
    // Request the first page
    (apiClient) =>
      apiClient.getMembershipRenewalFormConfigurations(
        business.BusinessId as string,
        {
          q: debouncedQuery,
          includeDeleted: showDeletedMembershipRenewal ? "true" : "false",
        }
      ),
    // Request subsequent pages
    (apiClient, _, continuationToken) =>
      apiClient.getMembershipRenewalFormConfigurations(
        business.BusinessId as string,
        {
          q: debouncedQuery,
          includeDeleted: showDeletedMembershipRenewal ? "true" : "false",
          continuationToken,
        }
      ),
    [business.BusinessId, debouncedQuery, showDeletedMembershipRenewal]
  );

  const deleteMembershipRenewalRequest = useAdminApiUpdateRequest(
    (apiClient, membershipRenewalId: string) =>
      apiClient.deleteMembershipRenewalFormConfiguration(
        business.BusinessId!,
        membershipRenewalId
      ),
    [membershipRenewals]
  );

  const restoreMembershipRenewalRequest = useAdminApiUpdateRequest(
    (apiClient, membershipRenewalId: string) =>
      apiClient.restoreMembershipRenewalFormConfiguration(
        business.BusinessId!,
        membershipRenewalId
      ),
    [membershipRenewals]
  );

  const onClickNewMembershipRenewal = () =>
    navigate(
      `/admin/businesses/${business.BusinessId}/membership-renewals/new`
    );

  const onClickEditMembershipRenewal = (
    membershipRenewal: CherryHubAdminApi.MembershipRenewalFormConfigurationSummaryItem
  ) =>
    navigate(
      `/admin/businesses/${business.BusinessId}/membership-renewals/${membershipRenewal.Meta?.Id}`
    );

  const onClickDeleteMembershipRenewal = useCallback(
    (row: CherryHubAdminApi.MembershipRenewalFormConfigurationSummaryItem) =>
      showConfirmationDialog(
        "Delete Membership Renewal?",
        `Are you sure you wish to delete this membership renewal form: ${row.DisplayName}?`,
        "Delete"
      ).then(({ ok }) => {
        if (ok) {
          deleteMembershipRenewalRequest.request(row.Meta?.Id!);
        }
      }),
    [membershipRenewals]
  );
  const onClickRestoreMembershipRenewal = useCallback(
    (row: CherryHubAdminApi.MembershipRenewalFormConfigurationSummaryItem) =>
      showConfirmationDialog(
        "Restore Membership Renewal?",
        `Are you sure you wish to restore this deleted membership renewal form: ${row.DisplayName}?`,
        "Restore"
      ).then(({ ok }) => {
        if (ok) {
          restoreMembershipRenewalRequest.request(row.Meta?.Id!);
        }
      }),
    [membershipRenewals]
  );

  useEffect(() => {
    if (restoreMembershipRenewalRequest.result) {
      if (restoreMembershipRenewalRequest.result.ok) {
        showSuccessToast("Membership renewal form successfully restored");
        refresh();
      } else {
        showErrorToast(
          restoreMembershipRenewalRequest.error ??
            "Failed to restore membership renewal form"
        );
      }
    }
  }, [restoreMembershipRenewalRequest.result, refresh]);

  useEffect(() => {
    if (deleteMembershipRenewalRequest.result) {
      if (deleteMembershipRenewalRequest.result.ok) {
        showSuccessToast("Membership renewal form successfully deleted");
        refresh();
      } else {
        showErrorToast(
          deleteMembershipRenewalRequest.error ??
            "Failed to delete membership renewal form"
        );
      }
    }
  }, [deleteMembershipRenewalRequest.result, refresh]);
  return (
    <>
      <PageHeading>
        <PageHeading.Title>Membership Renewal Forms</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}/membership-renewals`}
              >
                Membership Renewal Forms
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          <HStack w="100%">
            <Box flex="4" maxWidth="400px">
              {searchInputElement}
            </Box>
            <Box flex="1">
              <Checkbox
                checked={showDeletedMembershipRenewal}
                onChange={onChangeDeletedMembershipRenewalCheck}
                title="Include deleted membership renewal form the list."
              >
                Include deleted
              </Checkbox>
            </Box>
            <Box marginLeft="auto">
              <BusinessMenu
                businessId={business.Meta?.Id!}
                leftIcon={<PlusIcon />}
                onClick={onClickNewMembershipRenewal}
              >
                New Membership Renewal Form
              </BusinessMenu>
            </Box>
          </HStack>
          <MembershipRenewalTable
            membershipRenewals={membershipRenewals}
            isLoading={isLoading}
            disabledActions={false}
            debouncedQuery={debouncedQuery}
            onClickDeleteMembershipRenewal={onClickDeleteMembershipRenewal}
            onClickEditMembershipRenewal={onClickEditMembershipRenewal}
            onClickRestoreMembershipRenewal={onClickRestoreMembershipRenewal}
          />
          {confirmationElement}
        </VStack>
      </PageContent>
    </>
  );
};
